import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetContractCompliance,
  useDismissClauseSuggestion,
  getGetContractComplianceQueryKey,
  type PolicyRuleResult,
  type ClauseSuggestion,
} from "@workspace/api-client-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "wouter";
import {
  CheckCircle2,
  XCircle,
  AlertTriangle,
  Info,
  Shield,
  RefreshCw,
  BookOpen,
  ChevronDown,
  ChevronUp,
  X,
  EyeOff,
} from "lucide-react";

function SeverityIcon({ severity, status }: { severity: PolicyRuleResult["severity"]; status: PolicyRuleResult["status"] }) {
  if (status === "pass") return <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />;
  if (severity === "blocker") return <XCircle className="h-4 w-4 text-red-500 shrink-0" />;
  if (severity === "warning") return <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />;
  return <Info className="h-4 w-4 text-blue-500 shrink-0" />;
}

function severityRowClass(severity: PolicyRuleResult["severity"], status: PolicyRuleResult["status"]): string {
  if (status === "pass") return "bg-emerald-50/50 border-emerald-100";
  if (severity === "blocker") return "bg-red-50/50 border-red-200";
  if (severity === "warning") return "bg-amber-50/50 border-amber-200";
  return "bg-blue-50/50 border-blue-100";
}

interface DismissFormProps {
  suggestion: ClauseSuggestion;
  contractId: number;
  onDismissed: () => void;
  onCancel: () => void;
}

function DismissForm({ suggestion, contractId, onDismissed, onCancel }: DismissFormProps) {
  const [reason, setReason] = useState("");
  const dismissMutation = useDismissClauseSuggestion();

  const handleDismiss = async () => {
    if (!reason.trim()) return;
    try {
      await dismissMutation.mutateAsync({
        id: contractId,
        data: {
          reason: reason.trim(),
          ...(suggestion.clauseId ? { clauseId: suggestion.clauseId } : {}),
          ...(!suggestion.clauseId && suggestion.category ? { category: suggestion.category } : {}),
        },
      });
      onDismissed();
    } catch {
      // Error toast handled at higher level if needed; keep panel responsive.
    }
  };

  return (
    <div className="mt-2 space-y-2 bg-white/80 rounded-md border p-2">
      <Label className="text-xs">Dismissal reason (required)</Label>
      <Input
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="e.g. Not applicable for this contract type"
        className="h-7 text-xs"
        autoFocus
        onKeyDown={(e) => { if (e.key === "Enter") handleDismiss(); if (e.key === "Escape") onCancel(); }}
      />
      <div className="flex gap-1.5">
        <Button size="sm" className="h-6 text-xs px-2" onClick={handleDismiss} disabled={!reason.trim() || dismissMutation.isPending}>
          {dismissMutation.isPending ? "Dismissing…" : "Confirm dismiss"}
        </Button>
        <Button size="sm" variant="ghost" className="h-6 text-xs px-2" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

interface CompliancePanelProps {
  contractId: number;
  onHasBlockers?: (has: boolean) => void;
  onFailingRuleTypes?: (types: Set<string>) => void;
  refreshTrigger?: number;
}

export function CompliancePanel({ contractId, onHasBlockers, onFailingRuleTypes, refreshTrigger }: CompliancePanelProps) {
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(true);
  const [expandedRules, setExpandedRules] = useState<Set<number>>(new Set());
  const [dismissingIdx, setDismissingIdx] = useState<number | null>(null);

  const { data, isLoading, refetch } = useGetContractCompliance(contractId);

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: getGetContractComplianceQueryKey(contractId) });
    void refetch();
  };

  useEffect(() => {
    if (refreshTrigger !== undefined) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  useEffect(() => {
    if (!data) return;
    onHasBlockers?.(data.hasBlockers);
    if (onFailingRuleTypes) {
      const failing = new Set(
        data.ruleResults.filter((r) => r.status === "fail").map((r) => r.type),
      );
      onFailingRuleTypes(failing);
    }
  }, [data, onHasBlockers, onFailingRuleTypes]);

  const toggleRule = (id: number) => {
    setExpandedRules((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" /> Compliance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground animate-pulse py-4 text-center">Evaluating rules…</div>
        </CardContent>
      </Card>
    );
  }

  if (!data || (data.ruleResults.length === 0 && data.clauseSuggestions.length === 0)) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="h-4 w-4 text-muted-foreground" /> Compliance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center gap-2 py-6 text-center">
            <CheckCircle2 className="h-8 w-8 text-emerald-400" />
            <p className="text-sm text-muted-foreground">All compliance checks passed.</p>
            <Button variant="ghost" size="sm" asChild>
              <Link href="/settings#policies">Configure policy rules in Settings</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const overallStatus =
    data.summary.blockersFailing > 0
      ? "blocker"
      : data.summary.warningsFailing > 0
        ? "warning"
        : "pass";

  const activeSuggestions = data.clauseSuggestions.filter((s) => !s.dismissed);
  const dismissedSuggestions = data.clauseSuggestions.filter((s) => s.dismissed);

  return (
    <Card className={
      overallStatus === "blocker"
        ? "border-red-300"
        : overallStatus === "warning"
          ? "border-amber-300"
          : "border-emerald-300"
    }>
      <CardHeader className="pb-2 cursor-pointer" onClick={() => setExpanded((p) => !p)}>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            {overallStatus === "blocker" ? (
              <XCircle className="h-4 w-4 text-red-500" />
            ) : overallStatus === "warning" ? (
              <AlertTriangle className="h-4 w-4 text-amber-500" />
            ) : (
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
            )}
            Compliance
          </CardTitle>
          <div className="flex items-center gap-2">
            <div className="flex gap-1.5">
              {data.summary.blockersFailing > 0 && (
                <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">
                  {data.summary.blockersFailing} blocker{data.summary.blockersFailing !== 1 ? "s" : ""}
                </Badge>
              )}
              {data.summary.warningsFailing > 0 && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                  {data.summary.warningsFailing} warning{data.summary.warningsFailing !== 1 ? "s" : ""}
                </Badge>
              )}
              {data.summary.pass > 0 && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">
                  {data.summary.pass} passing
                </Badge>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={(e) => { e.stopPropagation(); refresh(); }}
            >
              <RefreshCw className="h-3.5 w-3.5 text-muted-foreground" />
            </Button>
            {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </div>
      </CardHeader>

      {expanded && (
        <CardContent className="space-y-4 pt-0">
          {data.hasBlockers && (
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800">
              <strong>This contract cannot be submitted for approval</strong> until all blocker violations are resolved.
            </div>
          )}

          <div className="space-y-2">
            {data.ruleResults.map((r) => (
              <div
                key={r.ruleId}
                className={`rounded-lg border overflow-hidden ${severityRowClass(r.severity, r.status)}`}
              >
                <div
                  className="flex items-start gap-2.5 p-3 cursor-pointer"
                  onClick={() => r.detail && toggleRule(r.ruleId)}
                >
                  <SeverityIcon severity={r.severity} status={r.status} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{r.ruleName}</span>
                      <span className={`text-xs capitalize px-1.5 py-0.5 rounded-full border ${
                        r.severity === "blocker" ? "bg-red-100 text-red-600 border-red-200"
                          : r.severity === "warning" ? "bg-amber-100 text-amber-600 border-amber-200"
                            : "bg-blue-100 text-blue-600 border-blue-200"
                      }`}>{r.severity}</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{r.message}</p>
                    <Button variant="link" size="sm" className="h-auto p-0 text-xs mt-0.5 text-muted-foreground hover:text-foreground" asChild>
                      <Link href="/settings#policies">View rule in Policy Rules →</Link>
                    </Button>
                  </div>
                  {r.detail && (
                    expandedRules.has(r.ruleId)
                      ? <ChevronUp className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                      : <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
                  )}
                </div>
                {r.detail && expandedRules.has(r.ruleId) && (
                  <div className="px-3 pb-3 pt-0">
                    <p className="text-xs text-muted-foreground bg-white/60 rounded p-2 border">
                      {r.detail}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {activeSuggestions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Missing Clauses</p>
              {activeSuggestions.map((s, i) => (
                <div key={`${s.clauseId ?? s.category}-${i}`} className={`rounded-lg border overflow-hidden ${s.required ? "border-red-200 bg-red-50/40" : "border-amber-200 bg-amber-50/40"}`}>
                  <div className="flex items-start gap-2.5 p-3">
                    <BookOpen className={`h-4 w-4 shrink-0 mt-0.5 ${s.required ? "text-red-500" : "text-amber-500"}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        {s.clauseTitle && <span className="text-sm font-medium">{s.clauseTitle}</span>}
                        {s.category && (
                          <Badge variant="outline" className="text-xs">
                            {s.category.replace(/_/g, " ")}
                          </Badge>
                        )}
                        {s.required
                          ? <Badge className="text-xs bg-red-100 text-red-600 border-red-200">Required</Badge>
                          : <Badge className="text-xs bg-amber-100 text-amber-600 border-amber-200">Suggested</Badge>
                        }
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{s.reason}</p>
                      {s.clauseId && (
                        <Button variant="link" size="sm" className="h-auto p-0 text-xs mt-1" asChild>
                          <Link href="/clause-library">View in clause library</Link>
                        </Button>
                      )}
                      {/* Inline dismiss form for optional suggestions only */}
                      {!s.required && dismissingIdx === i && (
                        <DismissForm
                          suggestion={s}
                          contractId={contractId}
                          onDismissed={() => { setDismissingIdx(null); refresh(); }}
                          onCancel={() => setDismissingIdx(null)}
                        />
                      )}
                    </div>
                    {!s.required && dismissingIdx !== i && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 shrink-0 text-muted-foreground hover:text-foreground"
                        title="Dismiss this suggestion"
                        onClick={() => setDismissingIdx(i)}
                      >
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {dismissedSuggestions.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/60 flex items-center gap-1">
                <EyeOff className="h-3 w-3" /> Dismissed suggestions ({dismissedSuggestions.length})
              </p>
              <div className="space-y-1">
                {dismissedSuggestions.map((s, i) => (
                  <div key={`dismissed-${s.clauseId ?? s.category}-${i}`} className="flex items-center gap-2 px-3 py-1.5 rounded border border-dashed border-muted-foreground/20 opacity-60 text-xs text-muted-foreground">
                    <EyeOff className="h-3 w-3 shrink-0" />
                    <span className="truncate">
                      {s.clauseTitle ?? s.category ?? "Clause"}
                      {s.dismissalReason && <> — <em>{s.dismissalReason}</em></>}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
